import { BaseProjectIdProps } from "@custom-types/base-types";
import { useEffect, useMemo, useRef } from "react";
import { SnapshotsTable } from "@pages/project-details/project-snapshots/snapshots-table";
import { ColumnPreferencesProvider } from "@src/context-providers/table/column-preferences-provider";
import {
  DEFAULT_SNAPSHOTS_SORT_BY,
  SNAPSHOTS_SORT_BY_MAP,
  SNAPSHOTS_SORT_OPTIONS,
} from "@pages/project-details/project-snapshots/snapshots-table-constants";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import {
  SnapshotsSortBy,
  SNAPSHOTS_TABLE_COLUMNS,
} from "@pages/project-details/project-snapshots/snapshots-table-types";
import { isSnapshotsSortBy } from "@custom-types/type-guards";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import {
  getSnapshotsSortBy,
  sortSnapshots,
} from "@pages/project-details/project-snapshots/snapshots-table-utils";
import { SortSelector } from "@components/common/sort-selector";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { searchSelector } from "@store/ui/ui-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { SnapshotsEmptyPage } from "@pages/project-details/project-snapshots/snapshots-empty-page";
import { BulkDeleteSnapshotsSuccessDialog } from "@pages/project-details/project-snapshots/bulk-actions/bulk-delete-snapshots-success-dialog";
import { fetchProjectSnapshots } from "@store/projects/projects-slice-thunk";
import {
  isFetchingProjectSnapshotsSelector,
  selectedProjectSnapshotsSelector,
} from "@store/projects/projects-selector";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useSnapshotBulkActions } from "@pages/project-details/project-snapshots/hooks/use-snapshot-bulk-actions";
import { getExportComponent } from "@utils/member-utils";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { useToast } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { SnapshotsMobileView } from "@pages/project-details/project-snapshots/snapshots-mobile-view";
import { ProjectSnapshotsKpi } from "@pages/project-details/project-snapshots/project-snapshots-kpi";
import { useSnapshotsBeingCreatedTracker } from "@pages/project-details/project-snapshots/hooks/use-snapshots-being-created-tracker";

/** Renders the project snapshots tab */
export function ProjectSnapshots({
  projectId,
}: BaseProjectIdProps): JSX.Element {
  const hasTrackedEvent = useRef(false);
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();

  const snapshots = useAppSelector(selectedProjectSnapshotsSelector);
  const isLoading = useAppSelector(isFetchingProjectSnapshotsSelector);
  const { debouncedSearchText } = useAppSelector(searchSelector);

  // Track how many snapshots are being created and reload the table once they finish
  useSnapshotsBeingCreatedTracker({ projectId });

  const { sortBy, setSortBy } = useSubjectsSortByQuery<SnapshotsSortBy>({
    defaultSort: DEFAULT_SNAPSHOTS_SORT_BY,
    isValidSort: isSnapshotsSortBy,
  });

  /** Returns the snapshots based on sorted and filtered criteria */
  const sortedFilteredSnapshots = useMemo(() => {
    return sortSnapshots(snapshots, sortBy).filter((snapshot) =>
      snapshot.name.toLowerCase().includes(debouncedSearchText.toLowerCase())
    );
  }, [snapshots, sortBy, debouncedSearchText]);

  const buttonComponents = useSnapshotBulkActions({
    snapshots: sortedFilteredSnapshots,
  });
  const { isScreenSmOrSmaller } = useMediaQueryList();

  // Fetch the snapshots of the project when the component mounts
  useEffect(() => {
    async function fetchSnapshots(): Promise<void> {
      try {
        await dispatch(fetchProjectSnapshots({ projectId })).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `ProjectSnapshots-fetchSnapshots-${Date.now().toString()}`,
          title: "Could not fetch project snapshots",
          error,
        });
      }
    }

    void fetchSnapshots();
  }, [dispatch, handleErrorWithToast, projectId, openToast]);

  // Track the event once when snapshots are loaded
  useEffect(() => {
    if (!isLoading && snapshots.length > 0 && !hasTrackedEvent.current) {
      hasTrackedEvent.current = true;
      trackEvent({
        name: SnapshotEvents.loadedSnapshots,
        props: { totalSnapshots: snapshots.length },
      });
    }
  }, [isLoading, snapshots.length, trackEvent]);

  const shouldShowEmptyPage = snapshots.length === 0 && !isLoading;

  /** True if there is at least one snapshot that the user can edit/remove */
  const hasAnyAdminSnapshot = sortedFilteredSnapshots.some(isUserSnapshotAdmin);

  return (
    <>
      {/*
       * Rendered at the page level (outside the empty-page/table branch) so the
       * bulk-delete success dialog survives the table unmounting when all
       * snapshots are deleted and the empty page is shown.
       */}
      <BulkDeleteSnapshotsSuccessDialog />

      {shouldShowEmptyPage ? (
        <SnapshotsEmptyPage debouncedSearchText={debouncedSearchText} />
      ) : (
        <>
          {snapshots.length > 0 && <ProjectSnapshotsKpi />}
          <ColumnPreferencesProvider
            tableKey="project-snapshots-table"
            columns={[...SNAPSHOTS_TABLE_COLUMNS]}
            options={{
              nonConfigurableColumns: ["options"],
              defaultVisibleColumns: ["name", "accessLevel", "created"],
            }}
          >
            <EntitiesToolbar
              toolbarEntityName="Snapshot"
              numberOfRows={sortedFilteredSnapshots.length}
              isLoading={isLoading}
              sortActions={
                sortedFilteredSnapshots.length > 0 && (
                  <SortSelector
                    sortOptions={SNAPSHOTS_SORT_OPTIONS}
                    sortItem={
                      SNAPSHOTS_SORT_BY_MAP[sortBy] ??
                      SNAPSHOTS_SORT_BY_MAP[DEFAULT_SNAPSHOTS_SORT_BY]
                    }
                    setSortItem={(newSortItem) =>
                      setSortBy(getSnapshotsSortBy(newSortItem))
                    }
                    subjectType="Snapshot"
                  />
                )
              }
              exportComponent={getExportComponent(
                sortedFilteredSnapshots.length,
                buttonComponents.export
              )}
            />

            {isScreenSmOrSmaller ? (
              <SnapshotsMobileView
                snapshots={sortedFilteredSnapshots}
                isLoading={isLoading}
                sortBy={sortBy}
                bulkActionButtons={buttonComponents}
              />
            ) : (
              <SnapshotsTable
                snapshots={sortedFilteredSnapshots}
                isLoading={isLoading}
                sortBy={sortBy}
                setSortBy={setSortBy}
                bulkActionButtons={buttonComponents}
                shouldHideCheckbox={!hasAnyAdminSnapshot}
              />
            )}
          </ColumnPreferencesProvider>
        </>
      )}
    </>
  );
}
